import React, { useEffect, useState, useCallback } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';

import { getMitreAlerts as getMitreAlertsService} from 'services/modules/soc/agent';

import Breadcrumbs from 'config/Breadcrumb';

import { Typography, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, CircularProgress, TablePagination, Tooltip, Button, MenuItem, FormControl, Select, InputLabel } from '@mui/material';
import WarningIcon from '@mui/icons-material/Warning';

const Dashboard = () => {

  // use Location hook 
  const location = useLocation();
  const endDate = location.state?.end;
  const startDate = location.state?.start;
  const queryParams = new URLSearchParams(location.search);
  const agentName = queryParams.get('agentname');
  const agentId = queryParams.get('agentid');

  // React Router
  const navigate = useNavigate();

  // Redux Dispatch
  const dispatch = useDispatch();

  // State Variables
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPath, setCurrentPath] = useState(['Security Alerts']);
  const [severity, setSeverity] = useState("all");
  const severityOptions = ["all", "low", "medium", "high", "critical"];

  // Redux Selectors
  const response = useSelector(state => state.socData.mitre);
  const selectedCompanyContainer = useSelector((state) => state.auth.companyData)

  // Handles to get the severity based on rule level
  const getSeverityLabel = useCallback((ruleLevel) => {
    if (ruleLevel >= 0 && ruleLevel <= 6) {
      return "Low";
    } else if (ruleLevel >= 7 && ruleLevel <= 8) {
      return "Medium";
    } else if (ruleLevel >= 9 && ruleLevel <= 11) {
      return "High";
    } else if (ruleLevel >= 12) {
      return "Critical";
    }
    return "";
  }, []);

  // Fetch data based on agentId, page, and rowsPerPage, and dispatch action to fetch alerts
  useEffect(() => {
    const fetchData = async () => {
      try {
        setLoading(true);  
        const params = { agentId: agentId, pagestart: page * rowsPerPage, limit: rowsPerPage, severity: severity, soc_env: selectedCompanyContainer?.soc_env, end: endDate, start: startDate };
        dispatch(getMitreAlertsService({ params })); 
        setCurrentPath(['Dashboard', 'Mitre Attack', agentName]);
      } catch (error) {
      } finally {
        setLoading(false);  
      }
    };

    if (agentId) {
      fetchData();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [agentId, page, rowsPerPage, severity, dispatch, endDate, startDate]);

  useEffect(() => {
    if (response && response.data) {
      const hits = response.data;
      setTotalCount(response.totalRecords || 0);

      const groupedData = hits.map(hit => {

        const date = new Date(hit._source.timestamp);
        let istDate;
        // Regex to match both formats: +0530 and +05:30
        const timezoneOffsetRegex = /[+-]\d{2}:?\d{2}$/;

        // Check if the timestamp contains a valid timezone offset (either +0530 or +05:30)
        if (hit?._source?.timestamp?.match(timezoneOffsetRegex)) {
          // If the timestamp includes "+0530" or "+05:30", it's already in IST
          istDate = date;
        } else if (hit._source.timestamp?.includes("z") || hit._source.timestamp?.includes("+0000")) {
          // If the timestamp is UTC (includes "z" or "+0000"), convert to IST
          istDate = new Date(date.getTime() + 5.5 * 60 * 60 * 1000);
        } else {
          // If the timestamp doesn't match any known formats, handle as needed
          istDate = date;
        }

        return {
          timestamp: istDate
            .toLocaleString("en-GB", {
              year: "numeric",
              month: "short",
              day: "numeric",
              hour: "2-digit",
              minute: "2-digit",
              second: "2-digit",
            })
            .replace(",", "")
            .replace(" at", " @"),
          agentName: hit._source.agent.name,
          ruleName: hit._source.rule.id,
          ruleDescription: hit._source.rule.description,
          ruleLevel: hit._source.rule.level,
          alertId: hit._id,
          agentip: hit._source.agent.ip,
          mitreid: hit._source?.rule?.mitre?.id?.[0],
          mitreTactics: hit._source?.rule?.mitre?.tactic?.join(', ') || "N/A"
        };
      });

      setData(groupedData);
    }
  }, [response]);

  // Handles Change Page
  const handleChangePage = (event, newPage) => {
    setPage(newPage);
  };

  // Handles  change in rows per page
  const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0); 
  };

  // Handles to navigate to view single alert
  const viewAlerts = (alert) => {
    let severity = getSeverityLabel(alert?.ruleLevel);
    navigate('/soc/view-security-alert', { state: { alertId: alert.alertId, name: 'mitre', timestamp: alert.timestamp, severity: severity } });
  };

  if (loading) {
    return (
      <div className='loader-container p-4'>
        <CircularProgress />
      </div>
    );
  }

  return (
    <div className='p-3'>
      <Breadcrumbs paths={currentPath} />
      <div>
        <Typography variant="h5" align="center">View Alerts</Typography>
      </div>
      <div className='d-flex justify-content-between'>
        <div className=" col-lg-2 col-md-4 col-sm-12 col-xs-12 search-ecs">
          <FormControl fullWidth size='small'>
            <InputLabel>Severity</InputLabel>
            <Select
              value={severity}
              label="Severity"
              onChange={(event) => { setSeverity(event.target.value); setPage(0) }}
            >
              {severityOptions.map(severity =>
                <MenuItem value={severity}>{severity.substring(0, 1).toUpperCase() + severity.slice(1)}</MenuItem>
              )}
            </Select>
          </FormControl>
        </div>
        <div>
          <Button
            variant="contained"
            className='backfont mt-3 form-card'
            onClick={() => window.history.back()}
          >
            Back
          </Button>
        </div>

      </div>
      <p className='my-2 mx-2'><strong>Alerts Range:</strong> <span className='text-primary' >{startDate.toLocaleString()} - {new Date(endDate).toLocaleString()}</span></p>


      <TableContainer className="mx-2">
        <Table>
          <TableHead>
            <TableRow>
              <TableCell align='center'>Incident Time</TableCell>
              <TableCell align='center'>Agent Name</TableCell>
              <TableCell align='center'>Agent IP</TableCell>
              <TableCell align='center'>Technique Id</TableCell>
              <TableCell align='center'>Tactics</TableCell>
              <TableCell>Description</TableCell>
              <TableCell align='center'>Severity</TableCell>
              <TableCell align='center'>Actions</TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {data.length === 0 ? (
              <TableRow>
                <TableCell colSpan={8} align="center">
                  <WarningIcon color="disabled" fontSize="small" />
                  No Data Available
                </TableCell>
              </TableRow>
            ) : (
              data.map((alert, index) => (
                <TableRow key={index}>
                  <TableCell align='center' className='text-primary' ><b>{alert.timestamp}</b></TableCell>
                  <TableCell align='center'>{alert.agentName}</TableCell>
                  <TableCell align='center' className='text-secondary' ><b>{alert.agentip}</b></TableCell>
                  <TableCell align='center'><b>{alert?.mitreid}</b></TableCell>
                  <TableCell align="center" className='wrap'>{alert.mitreTactics}</TableCell>
                  <TableCell className='wrap'>{alert.ruleDescription && alert.ruleDescription.length > 50 ? alert.ruleDescription.slice(0, 50) + '...' : alert.ruleDescription}</TableCell>
                  <TableCell align='center'>
                    <div className="rating-value">
                      <span
                        className={`${getSeverityLabel(alert.ruleLevel)
                          ? getSeverityLabel(alert.ruleLevel).toLowerCase()
                          : ""
                          }`}
                      >
                        {getSeverityLabel(alert.ruleLevel)}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell align='center'>
                    <Tooltip title="View" placement="top">
                      <img
                        src="/assets/viewicon.svg"
                        alt="View"
                        className='icons-style'
                        onClick={() => viewAlerts(alert)}
                      />
                    </Tooltip>
                  </TableCell>
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
      </TableContainer>

      <TablePagination
        rowsPerPageOptions={[20, 25, 50, 100, 250, 500]}
        component="div"
        count={totalCount}
        rowsPerPage={rowsPerPage}
        page={page}
        onPageChange={handleChangePage}
        onRowsPerPageChange={handleChangeRowsPerPage}
      />
    </div>
  );
};

export default Dashboard;
